import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  Link2,
  Save,
  Loader2,
  AlertCircle,
  X,
  PackagePlus,
  Check
} from 'lucide-react';
import axios from 'axios';
import { ProductAutocomplete } from './forms/ProductAutocomplete';
import { assetIdsPayload } from './forms/AssetIdEntryModal';
import { WarrantyDropdown } from './forms/WarrantyDropdown';
import { PaymentAllocationPicker } from './forms/PaymentAllocationPicker';
import { NoteAllocationPicker } from './forms/NoteAllocationPicker';
import { recalculateLineItem } from './lineItemCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep', 'Puducherry'
];

const emptyLineItem = () => ({
  key: Date.now() + Math.random(),
  product_id: '',
  serial_number: '',
  description: '',
  mrp: '',
  quantity: 1,
  unit_id: '',
  price_per_unit: '',
  discount_percentage: 0,
  discount_amount: 0,
  tax_id: '',
  tax_amount: 0,
  total_amount: 0,
  warranty_days: '',
  warranty_id: '',
  warranty_name: '',
  _asset_ids: [],
  _assets_confirmed: false,
  _item_ids: []
});

export const PurchaseBillCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();                      // present on /bills/:id/edit
  const isEdit = !!editId;
  const fromPO = location.state?.fromPO || null;
  const fromIDC = location.state?.fromIDC || null;
  const fromSource = fromPO || fromIDC || null; // whichever pre-fill source is active
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [dupWarning, setDupWarning] = useState('');
  const [loadingBill, setLoadingBill] = useState(isEdit);  // show spinner while fetching bill

  // Master data
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [units, setUnits] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [availablePayments, setAvailablePayments] = useState([]);
  const [paymentAllocations, setPaymentAllocations] = useState({});
  const [availableDebitNotes, setAvailableDebitNotes] = useState([]);
  const [debitNoteAllocations, setDebitNoteAllocations] = useState({});
  const [existingBills, setExistingBills] = useState([]);

  // Header
  const [vendorId, setVendorId] = useState(fromSource?.vendor_id || '');
  const [billNumber, setBillNumber] = useState(fromPO?.bill_number || '');
  const [billDate, setBillDate] = useState(fromSource?.bill_date || new Date().toISOString().split('T')[0]);
  const [stateOfSupply, setStateOfSupply] = useState(fromSource?.state_of_supply || '');
  const [companyId, setCompanyId] = useState(fromSource?.company_id || '');
  const [paymentTermId, setPaymentTermId] = useState(fromPO?.payment_term_id || '');
  const [paymentDueDate, setPaymentDueDate] = useState(fromPO?.payment_due_date || '');
  const [sourcePoId] = useState(fromPO?.source_po_id || null);
  const [sourcePoIds] = useState(fromPO?.source_po_ids || (fromPO?.source_po_id ? [fromPO.source_po_id] : []));
  const [sourcePoNumbers] = useState(fromPO?.source_po_numbers || (fromPO?.source_po_number ? [fromPO.source_po_number] : []));
  const [sourceIdcId] = useState(fromIDC?.source_idc_id || null);
  const [sourceIdcNumber] = useState(fromIDC?.source_idc_number || '');

  // Line items — pre-fill from PO or IDC
  const [lineItems, setLineItems] = useState(
    fromSource?.line_items?.length
      ? fromSource.line_items.map(li => ({
          key: Date.now() + Math.random(),
          product_id: li.product_id || '',
          serial_number: '',
          description: li.description || '',
          mrp: li.mrp || '',
          quantity: li.quantity || 1,
          unit_id: li.unit_id || '',
          price_per_unit: li.price_per_unit || '',
          discount_percentage: li.discount_percentage || 0,
          discount_amount: li.discount_amount || 0,
          tax_id: li.tax_id || '',
          tax_amount: li.tax_amount || 0,
          total_amount: li.total_amount || 0,
          warranty_days: '',
          warranty_id: li.warranty_id || '',
          warranty_name: li.warranty_name || '',
          _asset_ids: li.asset_ids || [],
          _assets_confirmed: (li.asset_ids || []).some(i => i),
          _item_ids: li.item_ids || []
        }))
      : [emptyLineItem()]
  );

  // Asset ID modal
  const [assetModalIdx, setAssetModalIdx] = useState(null);
  const [assetModalIds, setAssetModalIds] = useState([]);

  // Payment
  const [payments, setPayments] = useState(
    fromPO?.payments?.length
      ? fromPO.payments.map((p, i) => ({ key: Date.now() + i, payment_type: p.payment_type || 'Cash', amount: p.amount || 0, bank_detail_id: p.bank_detail_id || null }))
      : [{ key: 1, payment_type: 'Cash', amount: 0 }]
  );
  const [description, setDescription] = useState(fromSource?.description || '');
  const [roundOff, setRoundOff] = useState(fromSource?.round_off !== undefined ? fromSource.round_off : true);
  const [isPaid, setIsPaid] = useState(!!(fromPO?.paid_amount && fromPO.paid_amount > 0));
  const [paidAmount, setPaidAmount] = useState(fromPO?.paid_amount ? String(fromPO.paid_amount) : '');

  // File upload
  const [billFile, setBillFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [billFileUrl, setBillFileUrl] = useState('');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [vendorsRes, companiesRes, productsRes, taxesRes, unitsRes, ptRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/payment-terms`, getAuthHeaders())
        ]);
        setVendors(vendorsRes.data.filter(v => v.active));
        setCompanies(companiesRes.data.filter(c => c.active));
        setProducts(productsRes.data);
        setTaxes(taxesRes.data.filter(t => t.active));
        setUnits(unitsRes.data.filter(u => u.active));
        setPaymentTerms(ptRes.data.filter(p => p.active));
        const pmRes = await axios.get(`${BACKEND_URL}/api/payment-modes`, getAuthHeaders());
        setPaymentModes(pmRes.data.filter(p => p.active));
        const wRes = await axios.get(`${BACKEND_URL}/api/warranties`, getAuthHeaders());
        setWarranties(wRes.data || []);
        const billsRes = await axios.get(`${BACKEND_URL}/api/purchase-bills`, getAuthHeaders());
        setExistingBills(billsRes.data || []);
        const txnRes = await axios.get(`${BACKEND_URL}/api/transactions?type=payment_out`, getAuthHeaders());
        setAvailablePayments(txnRes.data || []);
        const notesRes = await axios.get(`${BACKEND_URL}/api/debit-notes`, getAuthHeaders());
        setAvailableDebitNotes(notesRes.data || []);
      } catch (err) {
        setError('Failed to load master data');
      }
    };
    fetchMasterData();
  }, []);

  useEffect(() => {
    setPaymentAllocations({});
    setDebitNoteAllocations({});
  }, [vendorId, companyId]);

  const useDebitNotes = !isEdit && payments.some(payment => payment.payment_type === 'Debit Note');

  useEffect(() => {
    if (!useDebitNotes) setDebitNoteAllocations({});
  }, [useDebitNotes]);

  // Auto-calculate payment due date from bill date + payment term days
  useEffect(() => {
    if (!paymentTermId || !billDate) return;
    const term = paymentTerms.find(pt => pt.id === paymentTermId);
    if (term?.days) {
      const base = new Date(billDate);
      base.setDate(base.getDate() + term.days);
      setPaymentDueDate(base.toISOString().split('T')[0]);
    } else {
      setPaymentDueDate('');
    }
  }, [paymentTermId, billDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    // Don't auto-fill bill number if we already have one (e.g. from PO conversion)
    if (billNumber) return;
    axios.get(`${BACKEND_URL}/api/invoice-config/${companyId}/next-doc-number/pb`, getAuthHeaders())
      .then(res => { if (res.data.doc_number) setBillNumber(res.data.doc_number); }).catch(() => {});
  }, [companyId]);

  // In edit mode: fetch bill and pre-fill all form fields
  useEffect(() => {
    if (!isEdit || !editId) return;
    const go = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/purchase-bills/${editId}`, getAuthHeaders());
        const b = res.data;
        setVendorId(b.vendor_id || '');
        setBillNumber(b.bill_number || '');
        setBillDate(b.bill_date || new Date().toISOString().split('T')[0]);
        setStateOfSupply(b.state_of_supply || '');
        setCompanyId(b.company_id || '');
        setPaymentTermId(b.payment_term_id || '');
        setPaymentDueDate(b.payment_due_date || '');
        setDescription(b.description || '');
        setRoundOff(b.round_off !== undefined ? b.round_off : true);
        setBillFileUrl(b.bill_file_url || '');
        if (b.line_items?.length) {
          setLineItems(b.line_items.map(li => ({
            key: Date.now() + Math.random(),
            product_id: li.product_id || '',
            serial_number: li.serial_number || '',
            description: li.description || '',
            mrp: li.mrp || '',
            quantity: li.quantity || 1,
            unit_id: li.unit_id || '',
            price_per_unit: li.price_per_unit || '',
            discount_percentage: li.discount_percentage || 0,
            discount_amount: li.discount_amount || 0,
            tax_id: li.tax_id || '',
            tax_amount: li.tax_amount || 0,
            total_amount: li.total_amount || 0,
            warranty_days: li.warranty_days || '',
            warranty_id: li.warranty_id || '',
            warranty_name: li.warranty_name || '',
            _asset_ids: li.asset_ids || [],
            _assets_confirmed: false,
            _item_ids: li.item_ids || [],
          })));
        }
        if (b.paid_amount > 0) {
          setIsPaid(true);
          setPaidAmount(String(b.paid_amount));
        }
        if (b.payments?.length) {
          setPayments(b.payments.map((p, i) => ({ key: Date.now() + i, payment_type: p.payment_type || 'Cash', amount: p.amount || 0, bank_detail_id: p.bank_detail_id || null })));
        }
      } catch {
        setError('Failed to load purchase bill');
      } finally {
        setLoadingBill(false);
      }
    };
    go();
  }, [editId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate line item
  const recalcLine = (item, changedField = null) => recalculateLineItem(item, taxes, changedField);

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'product_id') {
        const prod = products.find(p => p.id === value);
        if (prod && prod.unit_id) {
          updated[index].unit_id = prod.unit_id;
        }
        updated[index]._asset_ids = [];
        updated[index]._assets_confirmed = false;
        updated[index]._item_ids = [];
        if (value && !updated[updated.length - 1]?.product_id) return updated.map(i => recalcLine(i));
        if (value) updated.push(emptyLineItem());
      }
      if (field === 'quantity') {
        updated[index]._asset_ids = [];
        updated[index]._assets_confirmed = false;
        const qty = Math.max(0, parseInt(value) || 0);
        updated[index]._item_ids = (updated[index]._item_ids || []).slice(0, qty);
      }
      updated[index] = recalcLine(updated[index], field);
      return updated;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem()]);

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // Asset ID modal
  const openAssetModal = (idx) => {
    const li = lineItems[idx];
    if (!li.product_id) return;
    const qty = parseInt(li.quantity) || 1;
    setAssetModalIdx(idx);
    setAssetModalIds(li._asset_ids.length === qty ? [...li._asset_ids] : Array(qty).fill(''));
  };

  const confirmAssetIds = () => {
    const ids = assetModalIds.map(s => s.trim());
    setLineItems(prev => {
      const u = [...prev];
      u[assetModalIdx] = { ...u[assetModalIdx], _asset_ids: ids, _assets_confirmed: ids.some(id => id !== '') };
      return u;
    });
    setAssetModalIdx(null);
  };

  const addPayment = () => setPayments(prev => [...prev, { key: Date.now(), payment_type: 'Cash', amount: 0 }]);

  const updatePayment = (index, field, value) => {
    if (field === 'payment_type' && value === 'Debit Note') {
      setIsPaid(false);
      setPaidAmount('');
    }
    setPayments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removePayment = (index) => {
    if (payments.length <= 1) return;
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  // Calculations
  const subtotal = lineItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  const roundOffAmount = roundOff ? Math.round(subtotal) - subtotal : 0;
  const total = roundOff ? Math.round(subtotal) : Math.round(subtotal * 100) / 100;
  const directPaid = isPaid ? (parseFloat(paidAmount) || total) : 0;
  const matchingPayments = availablePayments.filter(payment =>
    payment.type === 'payment_out'
    && payment.vendor_id === vendorId
    && payment.company_id === companyId
    && (payment.unallocated_amount || 0) > 0
  );
  const matchingDebitNotes = availableDebitNotes.filter(note =>
    note.vendor_id === vendorId
    && note.company_id === companyId
    && (note.available_amount || 0) > 0
  );
  const allocatedPaymentAmount = Object.values(paymentAllocations).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
  const allocatedDebitNoteAmount = Object.values(debitNoteAllocations).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
  const balance = Math.round((total - directPaid - allocatedPaymentAmount - allocatedDebitNoteAmount) * 100) / 100;

  const totalQty = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalDiscount = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);

  const handleUploadBill = async () => {
    if (!billFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', billFile);
      const res = await axios.post(`${BACKEND_URL}/api/purchase-bills/upload-bill`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setBillFileUrl(res.data.file_url);
      setBillFile(null);
    } catch (err) {
      setError('Failed to upload bill file');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setDupWarning('');
    if (!vendorId) { setError('Please select a vendor'); return; }
    if (!companyId) { setError('Please select a company'); return; }
    if (!lineItems.some(li => li.product_id)) { setError('Please add at least one product'); return; }
    if (directPaid + allocatedPaymentAmount + allocatedDebitNoteAmount > total + 0.001
      || Object.entries(paymentAllocations).some(([id, amount]) => {
        const payment = matchingPayments.find(item => item.id === id);
        return !payment || (parseFloat(amount) || 0) <= 0 || parseFloat(amount) > payment.unallocated_amount;
      })) {
      setError('Payment allocations must fit within the bill balance and each payment’s available amount');
      return;
    }
    if (Object.entries(debitNoteAllocations).some(([id, amount]) => {
      const note = matchingDebitNotes.find(item => item.id === id);
      return !note || (parseFloat(amount) || 0) <= 0 || parseFloat(amount) > note.available_amount;
    })) {
      setError('Debit Note allocations must fit within the bill balance and each note’s available amount');
      return;
    }

    // Duplicate check: same bill_number + same vendor_id (skip in edit mode for same bill)
    if (!isEdit && billNumber && vendorId) {
      const duplicate = existingBills.find(
        b => (b.bill_number || '').trim().toLowerCase() === billNumber.trim().toLowerCase() && b.vendor_id === vendorId
      );
      if (duplicate) {
        setDupWarning('A purchase bill with this bill number and vendor already exists.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        vendor_id: vendorId,
        bill_number: billNumber,
        bill_date: billDate,
        state_of_supply: stateOfSupply,
        company_id: companyId,
        payment_term_id: paymentTermId || null,
        payment_due_date: paymentDueDate || null,
        source_po_id: sourcePoId || null,
        source_po_ids: sourcePoIds,
        source_idc_id: sourceIdcId || null,
        line_items: lineItems.filter(li => li.product_id).map(li => ({
          product_id: li.product_id,
          description: li.description,
          mrp: parseFloat(li.mrp) || null,
          quantity: parseFloat(li.quantity) || 0,
          unit_id: li.unit_id || null,
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: li.discount_amount,
          tax_id: li.tax_id || null,
          tax_amount: li.tax_amount,
          total_amount: li.total_amount,
          warranty_days: li.warranty_days ? parseInt(li.warranty_days) : null,
          warranty_id: li.warranty_id || null,
          warranty_name: li.warranty_name || null,
          asset_ids: assetIdsPayload(li._asset_ids, li.quantity),
          item_ids: (li._item_ids || []).filter(id => id.trim() !== '').length > 0 ? li._item_ids.filter(id => id.trim() !== '') : null
        })),
        payments: payments.filter(p => p.payment_type !== 'Debit Note').map(p => ({
          payment_type: p.payment_type,
          amount: parseFloat(p.amount) || 0,
          bank_detail_id: p.bank_detail_id || null
        })),
        description: description || null,
        round_off: roundOff,
        round_off_amount: Math.round(roundOffAmount * 100) / 100,
        subtotal: Math.round(subtotal * 100) / 100,
        total,
        paid_amount: directPaid,
        balance,
        payment_allocations: Object.entries(paymentAllocations).map(([transaction_id, allocationAmount]) => ({
          transaction_id,
          amount: parseFloat(allocationAmount) || 0,
        })),
        debit_note_allocations: Object.entries(debitNoteAllocations).map(([note_id, allocationAmount]) => ({
          note_id,
          amount: parseFloat(allocationAmount) || 0,
        })),
        bill_file_url: billFileUrl || null
      };

      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/purchase-bills/${editId}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/purchase-bills`, payload, getAuthHeaders());
      }
      navigate('/admin/purchase/bills');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save purchase bill');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="purchase-bill-create-page" className="pb-24">
      {/* Back */}
      <button onClick={() => navigate(isEdit ? `/admin/purchase/bills/${editId}` : '/admin/purchase/bills')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> {isEdit ? 'Back to Bill' : 'Back to Purchase Bills'}
      </button>

      {loadingBill ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : (
      <>

      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-8">
        {isEdit ? 'Edit Purchase Bill' : 'Create Purchase Bill'}
      </h1>

      {sourcePoId && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-sm mb-6" data-testid="from-po-banner">
          <Link2 className="h-4 w-4" />
          <span className="text-sm">
            Converting from {sourcePoNumbers.length > 1 ? 'Purchase Orders' : 'Purchase Order'}{sourcePoNumbers.length ? <strong className="ml-1">{sourcePoNumbers.join(', ')}</strong> : ''}. Review the pre-filled data and click Save to create the bill. {sourcePoNumbers.length > 1 ? 'All included POs' : 'The PO'} will be marked as converted.
          </span>
        </div>
      )}

      {sourceIdcId && (
        <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-sm mb-6" data-testid="from-idc-banner">
          <Link2 className="h-4 w-4" />
          <span className="text-sm">
            Converting from Inward Delivery Challan{sourceIdcNumber ? <strong className="ml-1">{sourceIdcNumber}</strong> : ''}. Only QC Passed items are pre-filled. Review and save to create the purchase bill.
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {dupWarning && (
        <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5 text-yellow-600" /><span>{dupWarning}</span>
          <button onClick={() => setDupWarning('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ========== HEADER SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Vendor */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Vendor <span className="text-red-500">*</span></label>
            <ProductAutocomplete value={vendorId} onChange={(id) => setVendorId(id)} products={vendors} testId="select-vendor" placeholder="Type vendor name..." />
          </div>
          {/* Bill Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Bill Number</label>
            <input type="text" value={billNumber} onChange={(e) => setBillNumber(e.target.value)} placeholder="e.g., TIS/25-26/001"
              data-testid="input-bill-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Bill Date */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Bill Date</label>
            <input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)}
              data-testid="input-bill-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* State of Supply */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State of Supply</label>
            <select value={stateOfSupply} onChange={(e) => setStateOfSupply(e.target.value)} data-testid="select-state"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company <span className="text-red-500">*</span></label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="select-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          {/* Payment Terms */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
            <select value={paymentTermId} onChange={(e) => setPaymentTermId(e.target.value)} data-testid="select-payment-term"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Payment Terms</option>
              {paymentTerms.map(pt => <option key={pt.id} value={pt.id}>{pt.name}{pt.days ? ` (${pt.days} days)` : ''}</option>)}
            </select>
          </div>
          {/* Payment Due Date — auto-filled from payment terms */}
          {paymentDueDate && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Due Date</label>
              <input type="date" value={paymentDueDate} onChange={e => setPaymentDueDate(e.target.value)}
                data-testid="input-payment-due-date"
                className="w-full px-3 py-2 border border-orange-300 bg-orange-50 rounded-sm focus:border-orange-500 focus:outline-none text-orange-900" />
              <p className="text-xs text-orange-600 mt-1">
                Auto-calculated from bill date + {paymentTerms.find(pt => pt.id === paymentTermId)?.days} days
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
          <button onClick={addLineItem} data-testid="add-line-item-btn"
            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm">
            <Plus className="h-4 w-4" /> Add Row
          </button>
        </div>

        {/* Table Header */}
        <div className="overflow-x-auto">
          <div className="min-w-[1300px]">
            <div className="grid gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.4fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
              <div>#</div>
              <div>Product</div>
              <div>MRP</div>
              <div>Qty</div>
              <div></div>
              <div>Unit</div>
              <div>Price/Unit</div>
              <div>Disc %</div>
              <div>Disc Amt</div>
              <div>Tax</div>
              <div>Tax Amt</div>
              <div>Amount</div>
              <div>Warranty</div>
            </div>

            {/* Line Item Rows */}
            {lineItems.map((item, idx) => (
              <div key={item.key} data-testid={`line-item-${idx}`}
                className="grid gap-2 px-4 py-2 border-b border-slate-100 items-center"
                style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.4fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
                {/* # */}
                <div className="flex items-center gap-1">
                  <span className="text-slate-500 text-sm">{idx + 1}</span>
                  {lineItems.length > 1 && (
                    <button onClick={() => removeLineItem(idx)} data-testid={`remove-line-${idx}`}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {/* Product */}
                <div>
                  <ProductAutocomplete
                    value={item.product_id}
                    onChange={(pid) => updateLineItem(idx, 'product_id', pid)}
                    products={products}
                    testId={`line-product-${idx}`}
                    placeholder="Type product name..."
                  />
                  <input type="text" value={item.serial_number} onChange={(e) => updateLineItem(idx, 'serial_number', e.target.value)}
                    placeholder="Serial Number" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  <input type="text" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Description" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                </div>
                {/* MRP */}
                <div>
                  <input type="number" value={item.mrp} onChange={(e) => updateLineItem(idx, 'mrp', e.target.value)}
                    placeholder="0" className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                {/* Qty */}
                <div>
                  <input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                    min="1" step="1" data-testid={`line-qty-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                {/* Asset ID button */}
                <div className="pt-1">
                  {item.product_id && (parseInt(item.quantity) || 0) > 0 && (
                    <button type="button" onClick={() => openAssetModal(idx)} data-testid={`line-asset-btn-${idx}`}
                      className={`p-1.5 rounded transition-colors ${item._assets_confirmed ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-orange-500 bg-orange-50 hover:bg-orange-100'}`}
                      title={item._assets_confirmed ? `${item._asset_ids.filter(id => id).length} asset IDs entered` : 'Enter Asset IDs'}>
                      {item._assets_confirmed ? <Check className="h-4 w-4" /> : <PackagePlus className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                {/* Unit */}
                <div>
                  <select value={item.unit_id} onChange={(e) => updateLineItem(idx, 'unit_id', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                {/* Price/Unit */}
                <div>
                  <input type="number" value={item.price_per_unit} onChange={(e) => updateLineItem(idx, 'price_per_unit', e.target.value)}
                    placeholder="0" step="0.01" data-testid={`line-price-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                {/* Disc % */}
                <div>
                  <input type="number" value={item.discount_percentage} onChange={(e) => updateLineItem(idx, 'discount_percentage', e.target.value)}
                    min="0" max="100" step="0.01"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                {/* Disc Amt */}
                <div>
                  <input type="number" value={item.discount_amount} onChange={(e) => updateLineItem(idx, 'discount_amount', e.target.value)}
                    min="0" step="0.01" data-testid={`line-discount-amount-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                {/* Tax */}
                <div>
                  <div className="flex items-center gap-1">
                    <select value={item.tax_id} onChange={(e) => updateLineItem(idx, 'tax_id', e.target.value)}
                      data-testid={`line-tax-${idx}`}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                      <option value="">-</option>
                      {taxes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                    </select>
                    {idx === 0 && item.tax_id && lineItems.filter(li => li.product_id).length > 1 && (
                      <button type="button" data-testid="apply-tax-all-btn" title="Apply this tax to all items"
                        onClick={() => setLineItems(prev => prev.map(li => li.product_id ? recalcLine({ ...li, tax_id: item.tax_id }) : li))}
                        className="shrink-0 px-2 py-1.5 text-[10px] font-bold uppercase text-orange-600 border border-orange-300 rounded-sm hover:bg-orange-50 transition-colors whitespace-nowrap">
                        All
                      </button>
                    )}
                  </div>
                </div>
                {/* Tax Amt */}
                <div>
                  <span className="text-sm text-slate-600 px-2">{item.tax_amount?.toFixed(2)}</span>
                </div>
                {/* Amount */}
                <div>
                  <span className="text-sm font-medium text-slate-900 px-2">{item.total_amount?.toFixed(2)}</span>
                </div>
                {/* Warranty */}
                <div>
                  <WarrantyDropdown
                    value={item.warranty_id}
                    onChange={(wid, wname) => {
                      updateLineItem(idx, 'warranty_id', wid);
                      updateLineItem(idx, 'warranty_name', wname || '');
                    }}
                    warranties={warranties}
                    testId={`line-warranty-${idx}`}
                  />
                </div>
              </div>
            ))}

            {/* Totals Row */}
            <div className="grid gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm font-semibold"
              style={{ gridTemplateColumns: '0.5fr 2fr 1fr 0.7fr 0.4fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 1fr 0.8fr' }}>
              <div></div>
              <div className="text-slate-700">TOTAL</div>
              <div></div>
              <div className="text-slate-700">{totalQty}</div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div className="text-slate-700">{totalDiscount.toFixed(2)}</div>
              <div></div>
              <div className="text-slate-700">{totalTax.toFixed(2)}</div>
              <div className="text-slate-900">{subtotal.toFixed(2)}</div>
              <div></div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== PAYMENT & SUMMARY SECTION ========== */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left — Payment & Description */}
          <div className="space-y-4">
            {/* Payment entries */}
            {payments.map((p, idx) => (
              <div key={p.key} className="flex items-center gap-3">
                <div className="flex-1 space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Payment Mode</label>
                    <select value={p.payment_type} onChange={(e) => updatePayment(idx, 'payment_type', e.target.value)}
                      data-testid={`payment-type-${idx}`}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                      <option value="">Select</option>
                      {paymentModes.filter(pm => pm.name !== 'Debit Note').map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                      {!isEdit && <option value="Debit Note">Debit Note</option>}
                    </select>
                  </div>
                  {(p.payment_type === 'Online' || p.payment_type === 'UPI') && (() => {
                    const selectedCompany = companies.find(c => c.id === companyId);
                    if (!companyId || !selectedCompany) return (
                      <p className="text-xs text-amber-600">Please select a company first to see bank accounts</p>
                    );
                    const filterType = p.payment_type === 'Online' ? 'Net Banking' : 'UPI';
                    const filtered = (selectedCompany.bank_details || []).filter(bd => bd.type === filterType);
                    if (filtered.length === 0) return (
                      <p className="text-xs text-slate-400">No {filterType} accounts added for {selectedCompany.company_name}</p>
                    );
                    return (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {p.payment_type === 'Online' ? 'Bank Account' : 'UPI ID'}
                        </label>
                        <select value={p.bank_detail_id || ''} onChange={(e) => updatePayment(idx, 'bank_detail_id', e.target.value)}
                          data-testid={`bank-detail-${idx}`}
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                          <option value="">-- Select --</option>
                          {filtered.map(bd => (
                            <option key={bd.id} value={bd.id}>
                              {bd.type === 'Net Banking' ? `${bd.bank_name || ''} - ${bd.account_no || ''}` : bd.upi_id || bd.bank_name || ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                {payments.length > 1 && (
                  <button onClick={() => removePayment(idx)} className="mt-5 p-2 text-slate-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {!isEdit && (
              <>
                <PaymentAllocationPicker
                payments={matchingPayments}
                allocations={paymentAllocations}
                onChange={setPaymentAllocations}
                  documentBalance={Math.max(0, total - directPaid - allocatedDebitNoteAmount)}
                  label="Payment Out"
                />
                {useDebitNotes && (
                  <NoteAllocationPicker
                    notes={matchingDebitNotes}
                    allocations={debitNoteAllocations}
                    onChange={setDebitNoteAllocations}
                    documentBalance={Math.max(0, total - directPaid - allocatedPaymentAmount)}
                    label="Debit Note"
                  />
                )}
              </>
            )}

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Add Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} placeholder="Add a note or description..."
                data-testid="input-description"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm resize-none" />
            </div>
          </div>

          {/* Right — Summary */}
          <div className="space-y-3">
            {/* Round Off */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)}
                  data-testid="round-off-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                <span className="text-sm text-slate-600">Round Off</span>
                {roundOff && <span className="text-sm text-slate-500">{roundOffAmount >= 0 ? '+' : ''}{roundOffAmount.toFixed(2)}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Total</span>
                <span className="text-lg font-bold text-slate-900 min-w-[100px] text-right" data-testid="total-amount">
                  ₹{total.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              {/* Paid */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={isPaid} onChange={(e) => { setIsPaid(e.target.checked); if (e.target.checked) setPaidAmount(String(total)); }}
                    data-testid="paid-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                  <span className="text-sm text-slate-600">Paid</span>
                </div>
                {isPaid && (
                  <input type="number" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)}
                    data-testid="paid-amount" className="w-32 px-3 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                )}
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              {/* Balance */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">Balance</span>
                <span className={`text-lg font-bold min-w-[100px] text-right ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}
                  data-testid="balance-amount">
                  ₹{balance.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== ACTION BUTTONS ========== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between z-40">
        <div className="flex items-center gap-3">
          {/* Upload Bill */}
          <label data-testid="upload-bill-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 cursor-pointer transition-colors text-sm font-medium">
            <Upload className="h-4 w-4" />
            {billFileUrl ? 'Bill Uploaded' : 'Upload Bill'}
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => { setBillFile(e.target.files[0]); }} />
          </label>
          {billFile && !billFileUrl && (
            <button onClick={handleUploadBill} disabled={uploading}
              className="px-3 py-2.5 bg-slate-100 text-slate-700 rounded-sm text-sm hover:bg-slate-200">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Upload'}
            </button>
          )}

          {/* Link Payment */}
          <button data-testid="link-payment-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors text-sm font-medium">
            <Link2 className="h-4 w-4" />
            Link Payment
          </button>
        </div>

        <button onClick={handleSave} disabled={submitting} data-testid="save-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-2.5 rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isEdit ? 'Update Bill' : 'Save'}
        </button>
      </div>

      {/* ========== ASSET ID MODAL ========== */}
      {assetModalIdx !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Enter Asset IDs</h2>
              <button onClick={() => setAssetModalIdx(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-1">
                Product: <span className="font-medium text-slate-900">{products.find(p => p.id === lineItems[assetModalIdx]?.product_id)?.name || '-'}</span>
              </p>
              <p className="text-sm text-slate-500 mb-4">
                Enter {assetModalIds.length} Asset ID(s) for this line item. Leave blank to skip.
              </p>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {assetModalIds.map((val, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-6">{i + 1}.</span>
                    <input
                      type="text" value={val}
                      onChange={e => { const n = [...assetModalIds]; n[i] = e.target.value; setAssetModalIds(n); }}
                      placeholder="Enter Asset ID"
                      data-testid={`asset-modal-input-${i}`}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button onClick={() => setAssetModalIdx(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={confirmAssetIds}
                  data-testid="asset-modal-confirm"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 inline-flex items-center gap-2">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};
